import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Clapperboard, Film, Radio, UserCircle2, MoreHorizontal, BarChart3, Coins, Users, LifeBuoy, Rss, Swords, Lightbulb, Rocket, Crown, ChevronDown, BookOpen, HeartHandshake } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useNewBadges, NewBadge } from "@/components/nav/useNewBadges";

export const SECTIONS = [
  { id: "nav-create", to: "/dashboard", labelKey: "ns.create", descKey: "ns.create_d", icon: Clapperboard, paths: ["/dashboard", "/editor"] },
  { id: "nav-clips", to: "/clips", labelKey: "ns.clips", descKey: "ns.clips_d", icon: Film, paths: ["/clips", "/stats"],
    children: [{ id: "nav-stats", to: "/stats", labelKey: "nav.stats", icon: BarChart3 }] },
  { id: "nav-live", to: "/integrations", labelKey: "ns.live", descKey: "ns.live_d", icon: Radio, paths: ["/integrations"] },
  { id: "nav-account", to: "/credits", labelKey: "ns.account", descKey: "ns.account_d", icon: UserCircle2, paths: ["/credits", "/parrainage", "/workspace", "/support", "/guide", "/payment"],
    children: [
      { id: "nav-credits", to: "/credits", labelKey: "cr.title", icon: Coins },
      { id: "nav-referral", to: "/parrainage", labelKey: "ref.nav", icon: HeartHandshake },
      { id: "nav-workspace", to: "/workspace", labelKey: "nav.workspace", icon: Users },
      { id: "nav-guide", to: "/guide", labelKey: "nav.guide", icon: BookOpen },
      { id: "nav-support", to: "/support", labelKey: "nav.support", icon: LifeBuoy },
    ] },
];

const MORE = [
  { id: "nav-following", to: "/abonnements", labelKey: "nav.following", icon: Rss },
  { id: "nav-challenges", to: "/defis", labelKey: "nav.challenges", icon: Swords },
  { id: "nav-suggestions", to: "/suggestions", labelKey: "nav.suggestions", icon: Lightbulb },
  { id: "nav-releases", to: "/nouveautes", labelKey: "nav.releases", icon: Rocket },
];
const moreFor = (user) => (user?.is_superadmin ? [...MORE, { id: "nav-admin", to: "/admin", labelKey: "nav.admin", icon: Crown }] : MORE);
const inSection = (s, p) => s.paths.some((x) => p.startsWith(x));

const SubLink = ({ it, t, onClick, isNew }) => (
  <NavLink to={it.to} end onClick={onClick} data-testid={it.id}
    className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors ${isActive ? "text-white bg-white/[0.06]" : "text-slate-400 hover:text-white hover:bg-white/[0.03]"}`}>
    <it.icon className="w-3.5 h-3.5 shrink-0" />{t(it.labelKey)}
    {isNew?.([it.to]) && <NewBadge t={t} className="ml-auto" testid={`${it.id}-new`} />}
  </NavLink>
);

const MoreList = ({ t, user, onClick, isNew }) => (
  <div className="flex flex-col gap-0.5" data-testid="nav-more-list">{moreFor(user).map((it) => <SubLink key={it.id} it={it} t={t} onClick={onClick} isNew={isNew} />)}</div>
);

export const SideNav = ({ t, user }) => {
  const { pathname } = useLocation();
  const isNew = useNewBadges();
  const [more, setMore] = useState(() => MORE.some((m) => pathname.startsWith(m.to)) || pathname.startsWith("/admin"));
  const [pop, setPop] = useState(false);
  const moreNew = isNew(MORE.map((m) => m.to));
  return (
    <nav className="px-3 flex flex-col gap-1.5 mt-4" data-testid="side-nav">
      {SECTIONS.map((s) => {
        const on = inSection(s, pathname);
        return (
          <div key={s.id}>
            <NavLink to={s.to} data-testid={s.id} title={t(s.labelKey)}
              className={`relative cx-metal flex items-center justify-center lg:justify-start gap-3 px-4 py-3 rounded-lg ${on ? "cx-metal-active" : ""}`}>
              <s.icon className="w-[18px] h-[18px] shrink-0" />
              <span className="hidden lg:flex flex-col min-w-0">
                <span className="text-sm font-semibold leading-tight">{t(s.labelKey)}</span>
                <span className="text-[10.5px] text-slate-500 leading-snug truncate">{t(s.descKey)}</span>
              </span>
              {isNew(s.paths) && <NewBadge t={t} className="absolute -top-1.5 -right-1 lg:static lg:ml-auto" testid={`${s.id}-new`} />}
            </NavLink>
            {on && s.children && <div className="hidden lg:flex flex-col gap-0.5 ml-5 mt-1 pl-2 border-l border-white/10">{s.children.map((c) => <SubLink key={c.id} it={c} t={t} isNew={isNew} />)}</div>}
          </div>
        );
      })}
      <div className="hidden lg:block mt-1">
        <button onClick={() => setMore((m) => !m)} data-testid="nav-more-btn" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/[0.03] transition-colors">
          <MoreHorizontal className="w-[18px] h-[18px]" /><span className="flex-1 text-left">{t("ns.more")}</span>
          {moreNew && !more && <NewBadge t={t} testid="nav-more-new" />}
          <ChevronDown className={`w-4 h-4 transition-transform ${more ? "rotate-180" : ""}`} />
        </button>
        {more && <div className="ml-5 pl-2 border-l border-white/10"><MoreList t={t} user={user} isNew={isNew} /></div>}
      </div>
      <Popover open={pop} onOpenChange={setPop}>
        <PopoverTrigger asChild>
          <button data-testid="nav-more-btn-compact" title={t("ns.more")} className="lg:hidden cx-metal flex items-center justify-center px-4 py-3 rounded-lg"><MoreHorizontal className="w-[18px] h-[18px]" /></button>
        </PopoverTrigger>
        <PopoverContent side="right" className="w-56 bg-[#0b0e1a] border-white/10 p-2"><MoreList t={t} user={user} isNew={isNew} onClick={() => setPop(false)} /></PopoverContent>
      </Popover>
    </nav>
  );
};

export const MobileNav = ({ t, user }) => {
  const { pathname } = useLocation();
  const isNew = useNewBadges();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#06070e]/95 backdrop-blur-xl border-t border-white/10 grid grid-cols-5" data-testid="mobile-bottom-nav">
        {SECTIONS.map((s) => (
          <NavLink key={s.id} to={s.to} data-testid={`mobile-${s.id}`}
            className={`relative cx-metal flex flex-col items-center gap-1 py-2 m-1 rounded-lg text-[11px] ${inSection(s, pathname) ? "cx-metal-active" : ""}`}>
            <s.icon className="w-5 h-5" /><span className="truncate max-w-full px-1">{t(s.labelKey)}</span>
            {isNew(s.paths) && <NewBadge t={t} className="absolute -top-1 right-0" testid={`mobile-${s.id}-new`} />}
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} data-testid="mobile-nav-more" className="relative cx-metal flex flex-col items-center gap-1 py-2 m-1 rounded-lg text-[11px]">
          <MoreHorizontal className="w-5 h-5" /><span>{t("ns.more")}</span>
          {isNew([...MORE.map((m) => m.to), ...SECTIONS.flatMap((s) => (s.children || []).map((c) => c.to))]) && <NewBadge t={t} className="absolute -top-1 right-0" testid="mobile-nav-more-new" />}
        </button>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[#0b0e1a] border-white/10 rounded-t-2xl pb-8" data-testid="mobile-more-sheet">
          <SheetHeader><SheetTitle className="font-display text-white text-left">{t("ns.more")}</SheetTitle></SheetHeader>
          <div className="grid gap-4 mt-4">
            {SECTIONS.filter((s) => s.children).map((s) => (
              <div key={s.id}>
                <div className="cx-label mb-1">{t(s.labelKey)}</div>
                <div className="flex flex-col gap-0.5">{s.children.map((c) => <SubLink key={c.id} it={{ ...c, id: `mobile-${c.id}` }} t={t} onClick={close} isNew={isNew} />)}</div>
              </div>
            ))}
            <div><div className="cx-label mb-1">{t("ns.community")}</div><MoreList t={t} user={user} onClick={close} isNew={isNew} /></div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
